import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link
} from '@mui/material';
import { getSeasonName } from '../utils/dateUtils.js';

/**
 * Helper function to convert numbers to ordinals (1st, 2nd, 3rd, etc.)
 */
const getOrdinal = (num) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const mod100 = num % 100;
  return num + (suffixes[(mod100 - 20) % 10] || suffixes[mod100] || suffixes[0]);
};

/**
 * Simplified table view showing all selected courses for easy review
 */
const SimplifiedCourseTable = ({ groupedCourses, selectedIds, backupIds }) => {
  // Collect all selected courses and group multi-period courses
  const courseMap = new Map();
  
  Object.entries(groupedCourses).forEach(([groupKey, group]) => {
    group.courses.forEach(course => {
      const courseId = course.is_split_course
        ? `${course.semester_instance}_p${course.actual_period}`
        : course.semester_instance;
      
      const isSelected = selectedIds.has(courseId);
      const isBackup = backupIds.has(courseId);
      
      if (isSelected || isBackup) {
        // Use semester_instance as the grouping key to keep different semester instances separate
        // while still grouping multi-period courses within the same semester
        const courseKey = course.semester_instance;
        
        if (!courseMap.has(courseKey)) {
          courseMap.set(courseKey, {
            course_name: course.course_name,
            course_code: course.course_code,
            notes: course.notes,
            isCustomCourse: course.isCustomCourse,
            credits: 0,
            periods: [],
            isSelected: false,
            isBackup: false,
            originalCredits: course.original_credits || course.credits
          });
        }
        
        const consolidatedCourse = courseMap.get(courseKey);
        consolidatedCourse.credits += course.credits;
        consolidatedCourse.periods.push({
          year: group.year,
          semester: group.semester,
          period: group.period,
          credits: course.credits
        });
        
        // A course is "selected" if any part is selected, "backup" if all parts are backup
        if (isSelected) {
          consolidatedCourse.isSelected = true;
        } else if (isBackup && !consolidatedCourse.isSelected) {
          consolidatedCourse.isBackup = true;
        }
      }
    });
  });

  // Convert map to array and sort
  const selectedCourses = Array.from(courseMap.values());
  
  // Sort courses by their earliest occurrence
  selectedCourses.sort((a, b) => {
    const aEarliest = a.periods.sort((p1, p2) => {
      if (p1.year !== p2.year) return p1.year - p2.year;
      if (p1.semester !== p2.semester) return p1.semester - p2.semester;
      return p1.period - p2.period;
    })[0];
    
    const bEarliest = b.periods.sort((p1, p2) => {
      if (p1.year !== p2.year) return p1.year - p2.year;
      if (p1.semester !== p2.semester) return p1.semester - p2.semester;
      return p1.period - p2.period;
    })[0];
    
    if (aEarliest.year !== bEarliest.year) return aEarliest.year - bEarliest.year;
    if (aEarliest.semester !== bEarliest.semester) return aEarliest.semester - bEarliest.semester;
    return aEarliest.period - bEarliest.period;
  });

  if (selectedCourses.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Selected Courses
        </Typography>
        <Typography color="text.secondary">
          No courses selected yet. Use the toggle above to return to the course selection interface.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {selectedCourses.length} courses:
      </Typography>
      
      <TableContainer>
        <Table size="small" sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                  width: '25%'
                }}
              >
                Course Name
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                  width: '12%'
                }}
              >
                Code
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                  width: '8%'
                }}
              >
                Credits
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                  width: '20%'
                }}
              >
                When
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                  width: '10%'
                }}
              >
                Status
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                  width: '25%'
                }}
              >
                Notes
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedCourses.map((course, index) => {
              // Sort periods chronologically for display
              const sortedPeriods = [...course.periods].sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                if (a.semester !== b.semester) return a.semester - b.semester;
                return a.period - b.period;
              });

              return (
                <TableRow 
                  key={`${course.course_code}-${index}`}
                  sx={{
                    backgroundColor: course.isSelected 
                      ? 'rgba(25, 118, 210, 0.08)' 
                      : 'rgba(255, 193, 7, 0.08)'
                  }}
                >
                  <TableCell>
                    {course.course_name}
                    {course.periods.length > 1 && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        Multi-period course ({course.periods.length} periods)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Link
                      href={`https://www.uu.se/en/study/course?query=${course.course_code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      underline="hover"
                    >
                      {course.course_code}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {course.credits} credits
                  </TableCell>
                  <TableCell>
                    {sortedPeriods.map((period, periodIndex) => (
                      <div key={periodIndex}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getSeasonName(period.semester, period.year)}
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                          {getOrdinal(period.period)} Period ({period.credits} credits)
                        </Typography>
                        {periodIndex < sortedPeriods.length - 1 && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', my: 0.5 }}>
                            ···
                          </Typography>
                        )}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="caption" 
                      sx={{
                        color: course.isSelected ? 'success.main' : 'warning.main',
                        fontWeight: 500
                      }}
                    >
                      {course.isSelected ? 'Selected' : 'Backup'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {course.notes || ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default SimplifiedCourseTable; 